import { useLayoutEffect, useState } from 'react';
export function resolveResponsiveWorkspace({ width, height, coarsePointer = false, canHover = true }) {
  const safeWidth = Number.isFinite(width) && width > 0 ? width : 0;
  const safeHeight = Number.isFinite(height) && height > 0 ? height : 800;
  const touch = coarsePointer || !canHover;
  const short = safeHeight < 480;
  const mode = safeWidth < 640 || short ? 'compact' : safeWidth < 1024 ? 'medium' : safeWidth < 1440 || touch ? 'large' : safeWidth < 1680 ? 'desktop' : 'wide';
  return { mode, input: touch ? 'touch' : 'pointer', short };
}
/** Presentation only. Uses the actual root plus the visual viewport (zoom/keyboard), and restores its CSS property. */
export default function useResponsiveWorkspace(rootRef, enabled, portal = false) {
  const [layout, setLayout] = useState(() => resolveResponsiveWorkspace({width:0,height:800}));
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!enabled || !root) return undefined;
    const viewport = window.visualViewport;
    const coarse = window.matchMedia?.('(pointer: coarse)');
    const hover = window.matchMedia?.('(hover: hover)');
    const property = '--responsive-visible-height';
    const previous = root.style.getPropertyValue(property);
    const html = document.documentElement;
    const portalProperties = ['--office-visible-height', '--office-visual-center', '--office-visible-width', '--office-visual-left'];
    const previousPortal = portalProperties.map(key => html.style.getPropertyValue(key));
    let frame;
    const measure = () => {
      const rect = root.getBoundingClientRect();
      const viewportWidth = Number(viewport?.width) || window.innerWidth;
      const width = rect.width > 0 ? Math.min(rect.width, viewportWidth) : viewportWidth;
      const height = Number(viewport?.height) || window.innerHeight;
      const available = Math.max(180, height - Math.max(0, rect.top - (Number(viewport?.offsetTop) || 0)));
      const value = Math.floor(available) + 'px';
      if (root.style.getPropertyValue(property) !== value) root.style.setProperty(property, value);
      if (portal) {
        html.style.setProperty(portalProperties[0], Math.floor(height) + 'px');
        html.style.setProperty(portalProperties[1], Math.floor((Number(viewport?.offsetTop) || 0) + height / 2) + 'px');
        html.style.setProperty(portalProperties[2], Math.floor(viewportWidth) + 'px');
        html.style.setProperty(portalProperties[3], Math.max(0, Number(viewport?.offsetLeft) || 0) + 'px');
      }
      const next = resolveResponsiveWorkspace({width,height,coarsePointer:!!coarse?.matches,canHover:hover ? hover.matches : true});
      setLayout(current => current.mode === next.mode && current.input === next.input && current.short === next.short ? current : next);
    };
    const schedule = () => {
      if (frame !== undefined) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => { frame = undefined; measure(); });
    };
    measure();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;
    observer?.observe(root);
    window.addEventListener('resize',schedule);
    viewport?.addEventListener('resize',schedule);viewport?.addEventListener('scroll',schedule);
    for (const query of [coarse,hover]) {
      if (query?.addEventListener) query.addEventListener('change',schedule);
      else query?.addListener?.(schedule);
    }
    return () => {
      observer?.disconnect();if(frame !== undefined)cancelAnimationFrame(frame);
      window.removeEventListener('resize',schedule);
      viewport?.removeEventListener('resize',schedule);viewport?.removeEventListener('scroll',schedule);
      for (const query of [coarse,hover]) {
        if (query?.removeEventListener) query.removeEventListener('change',schedule);
        else query?.removeListener?.(schedule);
      }
      if (portal)portalProperties.forEach((key,index) => previousPortal[index] ? html.style.setProperty(key,previousPortal[index]) : html.style.removeProperty(key));
      if (previous)root.style.setProperty(property,previous);else root.style.removeProperty(property);
    };
  },[enabled,rootRef,portal]);
  return layout;
}
